import { useLayoutEffect, useState, type CSSProperties, type RefObject } from 'react';

/** Room a list needs below its anchor before it opens upwards instead. */
const MIN_BELOW = 200;
const MAX_HEIGHT = 384;

/**
 * A pop-up list (quick picker, "+ Usługa" menu) placed `fixed` under its anchor, so a table that scrolls sideways
 * (the quote in the event panel, owner 20.10) cannot clip it. Follows scrolling and resizing; opens upwards when
 * there is little room below (in the visual viewport when there is one); hidden while its anchor is scrolled out of view.
 * `matchWidth`: as wide as the anchor (a picker's box). `alignEnd`: the list's right edge under the anchor's (a "…" in the
 * last column, so it does not run off the window). Off (`enabled` false): undefined, the list keeps its CSS place.
 */
export function useFloating(anchor: RefObject<HTMLElement | null>, open: boolean, enabled: boolean, matchWidth = false, alignEnd = false): CSSProperties | undefined {
  const [style, setStyle] = useState<CSSProperties | undefined>(undefined);
  useLayoutEffect(() => {
    if (!enabled || !open) {
      setStyle(undefined);
      return;
    }
    const place = () => {
      const el = anchor.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      // what is really visible: the visual viewport when there is one (a phone's keyboard or a pinch zoom hides the rest)
      const vv = window.visualViewport;
      const top0 = vv ? vv.offsetTop : 0;
      const bottom0 = vv ? vv.offsetTop + vv.height : window.innerHeight;
      // the field scrolled out of view: its list goes too (it would hang over something else)
      if (r.bottom < top0 || r.top > bottom0) {
        setStyle({ position: 'fixed', visibility: 'hidden' });
        return;
      }
      const below = bottom0 - r.bottom - 8;
      const above = r.top - top0 - 8;
      const up = below < MIN_BELOW && above > below;
      setStyle({
        position: 'fixed',
        ...(alignEnd ? { left: 'auto', right: window.innerWidth - r.right } : { left: r.left, right: 'auto' }),
        ...(matchWidth ? { width: r.width } : {}),
        ...(up ? { top: 'auto', bottom: window.innerHeight - r.top + 4, maxHeight: Math.min(above, MAX_HEIGHT) } : { top: r.bottom + 4, bottom: 'auto', maxHeight: Math.min(below, MAX_HEIGHT) }),
        overflowY: 'auto',
      });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    window.visualViewport?.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      window.visualViewport?.removeEventListener('resize', place);
    };
  }, [anchor, open, enabled, matchWidth, alignEnd]);
  return style;
}
